import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getCheques } from "../cheques_proveedores/ApiServices";
import {
  extraerChequesFisicos,
  getChequesFisicos,
  getSucursalesChequesFisicos,
} from "../cheques_fisicos/ApiServices";
import {
  CUSTODIA,
  SUC_CASA_CENTRAL,
  idInstrumentoFisico,
  sucursalOpFisicosDesdeLista,
} from "../cheques_fisicos/chequesFisicos.constants";
import {
  abrevOrigenCheque,
  colorOrigenCheque,
  labelOrigenCheque,
} from "../cheques_proveedores/cheques.constants";
import { formatCuit, formatMoneda } from "./utils";

const PAGE_SIZE = 10;

const formatearFecha = (valor) => {
  const fecha = String(valor || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return "—";
  const [anio, mes, dia] = fecha.split("-");
  return `${dia}/${mes}/${anio}`;
};

export default function SeleccionChequesRecibidos({
  open,
  onClose,
  onConfirmar,
  idsExcluidos = [],
  chequesAsignadosOtros = [],
  restante = 0,
  proveedorLabel = "",
}) {
  const [busqueda, setBusqueda] = useState("");
  const [busquedaAplicada, setBusquedaAplicada] = useState("");
  const [page, setPage] = useState(0);
  const [cheques, setCheques] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [seleccionados, setSeleccionados] = useState([]);
  const [idSucursalFisicos, setIdSucursalFisicos] = useState(null);
  const [origen, setOrigen] = useState("echeq");

  const excluidos = useMemo(
    () => new Set((idsExcluidos || []).map((id) => String(id))),
    [idsExcluidos]
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (origen === "fisico") {
        if (!idSucursalFisicos) {
          setCheques([]);
          setTotal(0);
          setLoading(false);
          return;
        }
        const data = await getChequesFisicos({
          estado: "Disponible",
          id_sucursal: idSucursalFisicos,
          estado_custodia:
            idSucursalFisicos === SUC_CASA_CENTRAL
              ? CUSTODIA.EN_CASA_CENTRAL
              : CUSTODIA.EN_SUCURSAL,
          q: busquedaAplicada,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        });
        const lista = extraerChequesFisicos(data)
          .filter((c) => c?.acciones?.puede_usar_op !== false)
          .map((c) => ({
            ...c,
            id: idInstrumentoFisico(c),
            electronico: 0,
          }));
        setCheques(lista);
        setTotal(Number(data.total ?? lista.length));
      } else {
        const data = await getCheques({
          estado: "Disponible",
          electronico: 1,
          solo_endosables: 1,
          q: busquedaAplicada,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        });
        setCheques(Array.isArray(data.cheques) ? data.cheques : []);
        setTotal(Number(data.total ?? 0));
      }
    } catch (err) {
      setError(err.message || "No se pudieron cargar los cheques disponibles");
      setCheques([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [busquedaAplicada, page, origen, idSucursalFisicos]);

  useEffect(() => {
    if (!open) {
      setBusqueda("");
      setBusquedaAplicada("");
      setPage(0);
      setSeleccionados([]);
      setError("");
      setOrigen("echeq");
      setIdSucursalFisicos(null);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const sucursales = await getSucursalesChequesFisicos();
        if (cancelado) return;
        setIdSucursalFisicos(sucursalOpFisicosDesdeLista(sucursales));
      } catch {
        if (!cancelado) setIdSucursalFisicos(null);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    cargar();
  }, [open, cargar]);

  const disponibles = useMemo(
    () => cheques.filter((cheque) => !excluidos.has(String(cheque.id))),
    [cheques, excluidos]
  );

  const estaSeleccionado = (id) =>
    seleccionados.some((cheque) => String(cheque.id) === String(id));

  const toggleCheque = (cheque) => {
    setSeleccionados((prev) =>
      prev.some((item) => String(item.id) === String(cheque.id))
        ? prev.filter((item) => String(item.id) !== String(cheque.id))
        : [...prev, cheque]
    );
  };

  const buscar = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setBusquedaAplicada(busqueda.trim());
  };

  const importeSeleccionado = seleccionados.reduce(
    (acc, cheque) => acc + (Number(cheque.importe) || 0),
    0
  );

  const restanteNum = Number(restante) || 0;
  const seExcede = restanteNum > 0 && importeSeleccionado > restanteNum + 0.02;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Cheques disponibles en cartera
        {proveedorLabel ? (
          <Typography variant="body2" color="text.secondary" fontWeight={400}>
            Para: {proveedorLabel}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Solo se listan cheques en estado <strong>Disponible</strong> que se
          pueden endosar. Cada cheque se aplica por su importe completo.
          {origen === "fisico"
            ? " Los físicos en tránsito no aparecen: hay que recibirlos en Casa Central."
            : ""}
        </Alert>

        {idSucursalFisicos ? (
          <Tabs
            value={origen}
            onChange={(_e, value) => {
              setOrigen(value);
              setPage(0);
              setSeleccionados([]);
            }}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="E-cheq" value="echeq" />
            <Tab label="Cheques físicos" value="fisico" />
          </Tabs>
        ) : null}

        <Stack
          component="form"
          onSubmit={buscar}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            fullWidth
            label="Buscar"
            placeholder="N° de cheque, banco, librador…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <Button type="submit" variant="outlined" startIcon={<SearchIcon />}>
            Buscar
          </Button>
        </Stack>

        {restanteNum > 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Falta cubrir <strong>{formatMoneda(restanteNum)}</strong>.
          </Typography>
        ) : null}

        {chequesAsignadosOtros.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Ya asignados a otro proveedor
            </Typography>
            <TableContainer
              sx={{ border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#f9f9f9" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: "#f5f5f5" }} />
                    {["N° cheque", "Banco", "Importe", "Asignado a"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chequesAsignadosOtros.map((cheque) => (
                    <TableRow key={cheque.id} sx={{ opacity: 0.65 }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" disabled checked={false} />
                      </TableCell>
                      <TableCell>{cheque.nro_cheque ?? "—"}</TableCell>
                      <TableCell>{cheque.banco_emisor ?? "—"}</TableCell>
                      <TableCell>{formatMoneda(cheque.importe)}</TableCell>
                      <TableCell>
                        <Chip size="small" label="Ya utilizado" color="default" />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {cheque.razon_social}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {seExcede ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Los cheques elegidos suman {formatMoneda(importeSeleccionado)} y
            superan lo que falta cubrir. Ajustá la selección o compensá la
            diferencia con otro medio de pago.
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{ maxHeight: 320, border: "1px solid #e0e0e0", borderRadius: 1 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: "#f5f5f5" }} />
                    {[
                      "N° cheque",
                      "Banco",
                      "Importe",
                      "Fecha pago",
                      "Recibido de",
                      "Origen",
                      "Tipo",
                    ].map(
                      (h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {disponibles.map((cheque) => (
                    <TableRow
                      key={cheque.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => toggleCheque(cheque)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={estaSeleccionado(cheque.id)}
                          onChange={() => toggleCheque(cheque)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>{cheque.nro_cheque ?? "—"}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          title={cheque.banco_emisor}
                          sx={{ maxWidth: 180 }}
                        >
                          {cheque.banco_emisor ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatMoneda(cheque.importe)}</TableCell>
                      <TableCell>{formatearFecha(cheque.fecha_pago)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          title={cheque.recibido_de}
                          sx={{ maxWidth: 180 }}
                        >
                          {cheque.recibido_de || "—"}
                        </Typography>
                        {cheque.cuit_recibido ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatCuit(cheque.cuit_recibido)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={colorOrigenCheque(cheque)}
                          label={abrevOrigenCheque(cheque)}
                          title={labelOrigenCheque(cheque)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            Number(cheque.electronico) === 1 ? "E-cheque" : "Físico"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {disponibles.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 2, textAlign: "center" }}
              >
                No hay cheques disponibles para endosar con esta búsqueda.
              </Typography>
            ) : null}

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" sx={{ mr: "auto" }}>
          {seleccionados.length} seleccionado
          {seleccionados.length === 1 ? "" : "s"}
          {seleccionados.length > 0
            ? ` · ${formatMoneda(importeSeleccionado)}`
            : ""}
        </Typography>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={seleccionados.length === 0}
          onClick={() => {
            onConfirmar?.(seleccionados);
            onClose?.();
          }}
        >
          Agregar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
